/**
 * Utility functions for comparing data from different sources
 */

/**
 * Fields that should be compared between sources
 * Mapping between different naming conventions
 */
export const FIELD_MAPPINGS = {
    // Document Number variations
    'documentNumber': 'documentNumber',
    'document_number': 'documentNumber',
    'documentno': 'documentNumber',

    // Expiry Date variations
    'expiryDate': 'expiryDate',
    'expiry_date': 'expiryDate',
    'expirydate': 'expiryDate',

    // First Name variations
    'firstName': 'firstName',
    'first_name': 'firstName',
    'firstname': 'firstName',

    // Last Name variations
    'lastName': 'lastName',
    'last_name': 'lastName',
    'lastname': 'lastName'
};

export const COMPARABLE_FIELDS = [
    'documentNumber',
    'expiryDate',
    'firstName',
    'lastName'
];

/**
 * Normalize field names to a consistent format
 * @param {string} fieldName - The field name to normalize
 * @returns {string} - Normalized field name
 */
export const normalizeFieldName = (fieldName) => {
    const lowerFieldName = fieldName.toLowerCase().replace(/[\s_-]/g, '');
    return FIELD_MAPPINGS[fieldName] || FIELD_MAPPINGS[lowerFieldName] || fieldName;
};

/**
 * Normalize field values for comparison
 * @param {any} value - The value to normalize
 * @returns {string} - Normalized value
 */
export const normalizeValue = (value) => {
    if (!value || value === 'null' || value === 'N/A' || value === 'undefined') {
        return '';
    }

    // Convert to string and normalize
    let normalized = String(value).trim();

    // Remove extra whitespace and newlines for comparison
    normalized = normalized.replace(/\s+/g, ' ').toLowerCase();

    return normalized;
};

/**
 * Check if a field should be compared
 * @param {string} fieldName - The field name to check
 * @returns {boolean} - True if field should be compared
 */
export const isComparableField = (fieldName) => {
    const normalizedName = normalizeFieldName(fieldName);
    return COMPARABLE_FIELDS.includes(normalizedName);
};

/**
 * Compare two field values
 * @param {any} value1 - Value from source 1
 * @param {any} value2 - Value from source 2
 * @returns {boolean} - True if values match
 */
export const compareValues = (value1, value2) => {
    const normalized1 = normalizeValue(value1);
    const normalized2 = normalizeValue(value2);

    // Both empty values are considered a match
    if (!normalized1 && !normalized2) {
        return true;
    }

    // If one is empty and other is not, it's a mismatch
    if (!normalized1 || !normalized2) {
        return false;
    }

    return normalized1 === normalized2 ? true : normalized1.includes(normalized2);
};

/**
 * Get value from nested object using multiple possible field names
 * @param {object} obj - Object to search
 * @param {string} targetField - Target normalized field name
 * @returns {any} - Field value or undefined
 */
const getFieldValue = (obj, targetField) => {
    if (!obj) return undefined;

    // Find the actual key in the object that matches our target field
    for (const [key, value] of Object.entries(obj)) {
        if (normalizeFieldName(key) === targetField) {
            return value;
        }
    }

    return undefined;
};

/**
 * Get all unique comparable fields from multiple sources
 * @param {...Object} sources - Data source objects to compare
 * @returns {string[]} Array of normalized field names
 */
const getComparableFields = (...sources) => {
    // Filter out null/undefined sources
    const validSources = sources.filter(source => source != null);
    if (validSources.length < 2) return [];

    // Get all field names from all sources
    const allFields = new Set();
    validSources.forEach(source => {
        Object.keys(source).forEach(key => {
            const normalizedKey = normalizeFieldName(key);
            // Only add if the field exists in at least one other source
            const existsInOtherSource = validSources.some(otherSource =>
                otherSource !== source &&
                Object.keys(otherSource).some(otherKey =>
                    normalizeFieldName(otherKey) === normalizedKey
                )
            );
            if (existsInOtherSource) {
                allFields.add(normalizedKey);
            }
        });
    });

    // Include legacy comparable fields for backward compatibility
    COMPARABLE_FIELDS.forEach(field => allFields.add(field));

    return Array.from(allFields);
};

/**
 * Compare three data sources (OCR, Barcode, SOR)
 * @param {object} ocrData - OCR attributes
 * @param {object} barcodeData - Barcode attributes  
 * @param {object} sorData - SOR/MV data
 * @returns {object} - Comparison results for each comparable field
 */
export const compareThreeSources = (ocrData, barcodeData, sorData) => {
    const comparisons = {};
    const fieldsToCompare = getComparableFields(ocrData, barcodeData, sorData);
    console.log('[compareThreeSources] Dynamic fields to compare:', fieldsToCompare);

    fieldsToCompare.forEach(fieldName => {
        const ocrValue = getFieldValue(ocrData, fieldName);
        const barcodeValue = getFieldValue(barcodeData, fieldName);
        const sorValue = getFieldValue(sorData, fieldName);

        // Determine if all sources match
        const ocrBarcodeMatch = compareValues(ocrValue, barcodeValue);
        const ocrSorMatch = compareValues(ocrValue, sorValue);
        const barcodeSorMatch = compareValues(barcodeValue, sorValue);
        const allMatch = ocrBarcodeMatch && ocrSorMatch && barcodeSorMatch;

        comparisons[fieldName] = {
            allMatch,
            ocrValue,
            barcodeValue,
            sorValue,
            normalizedOcr: normalizeValue(ocrValue),
            normalizedBarcode: normalizeValue(barcodeValue),
            normalizedSor: normalizeValue(sorValue),
            matches: {
                ocrBarcode: ocrBarcodeMatch,
                ocrSor: ocrSorMatch,
                barcodeSor: barcodeSorMatch
            }
        };

        // Debugging log
        console.log('[compareThreeSources] field:', fieldName,
            'ocr:', ocrValue, 'barcode:', barcodeValue, 'sor:', sorValue,
            'allMatch:', allMatch, 'matches:', comparisons[fieldName].matches);
    });

    return comparisons;
};

/**
 * Get comparison status for all comparable fields (two sources)
 * @param {object} source1Data - Data from source 1
 * @param {object} source2Data - Data from source 2
 * @returns {object} - Object with field comparison results
 */
export const getFieldComparisons = (source1Data, source2Data) => {
    const comparisons = {};

    if (!source1Data || !source2Data) {
        return comparisons;
    }

    console.log('[getFieldComparisons] source1Data:', source1Data);
    console.log('[getFieldComparisons] source2Data:', source2Data);

    const fieldsToCompare = getComparableFields(source1Data, source2Data);
    console.log('[getFieldComparisons] Dynamic fields to compare:', fieldsToCompare);


    fieldsToCompare.forEach(fieldName => {
        const value1 = getFieldValue(source1Data, fieldName);
        const value2 = getFieldValue(source2Data, fieldName);
        const matches = compareValues(value1, value2);

        comparisons[fieldName] = {
            normalizedFieldName: fieldName,
            matches: matches,
            source1Value: value1,
            source2Value: value2
        };

        console.log('[getFieldComparisons] field:', fieldName, 'val1:', value1, 'val2:', value2, 'matches:', matches);
    });

    return comparisons;
};

/**
 * Get display-friendly field name
 * @param {string} fieldName - The field name
 * @returns {string} - Display-friendly name
 */
export const getDisplayFieldName = (fieldName) => {
    const displayNames = {
        'documentNumber': 'Document Number',
        'expiryDate': 'Expiry Date',
        'firstName': 'First Name',
        'lastName': 'Last Name'
    };

    const normalized = normalizeFieldName(fieldName);
    // If we have a preset display name, use it
    if (displayNames[normalized]) {
        return displayNames[normalized];
    }

    // Otherwise, generate a display name from the field name
    return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};